import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export const HEADERBUTTONBLOCK_LEGACY_FIELD_ORDER = [
  "label",
  "link_url",
  { type: "separator" },
  "background_color",
  "text_color",
  "font_size",
  "font_weight",
  { type: "separator" },
  "display_on_desktop",
  "display_on_mobile",
  "label_mobile",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  headerButtonBlockDescriptor: _generatedHeaderButtonBlockDescriptor,
  headerButtonBlockBlocDefinition,
  headerButtonBlockDashboardLayout,
  headerButtonBlockConfig,
  headerButtonBlockResolveConfig,
  headerButtonBlockProperties,
} = doodBlockConfig(
  {
    name: "Header - Button",
    componentType: "HeaderButtonBlockComponent",
    category: "navigation",
    description: "Header - Button.",
    keywords: [],
    allowedIn: [
      { bloc: "HeaderBlockComponent", slot: "left_blocks" },
      { bloc: "HeaderBlockComponent", slot: "right_blocks" },
      { bloc: "HeaderBlockComponent", slot: "navigation_links" },
    ],
    sharedFields: {},
  },

  () => ({
    label: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.header-button.label.label",
      },
    }),
    link_url: propertyValue({
      type: "link",
      value: "",
      options: {
        label: "pages.blocks.header-button.link-url.label",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-100" },
      options: {
        label: "pages.blocks.header-button.background-color.label",
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.header-button.text-color.label",
      },
    }),
    font_size: propertyValue({
      type: "select",
      value: "sm",
      options: {
        label: "pages.blocks.header-button.font-size.label",
        choices: [
          {
            value: "sm",
            label: "pages.blocks.header-button.font-size.options.sm",
          },
          {
            value: "base",
            label: "pages.blocks.header-button.font-size.options.base",
          },
          {
            value: "lg",
            label: "pages.blocks.header-button.font-size.options.lg",
          },
          {
            value: "xl",
            label: "pages.blocks.header-button.font-size.options.xl",
          },
          {
            value: "2xl",
            label: "pages.blocks.header-button.font-size.options.2xl",
          },
        ],
      },
    }),
    font_weight: propertyValue({
      type: "select",
      value: "normal",
      options: {
        label: "pages.blocks.header-button.font-weight.label",
        choices: [
          {
            value: "normal",
            label: "pages.blocks.header-button.font-weight.options.normal",
          },
          {
            value: "bold",
            label: "pages.blocks.header-button.font-weight.options.bold",
          },
        ],
      },
    }),
    display_on_desktop: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.header-button.display-on-desktop.label",
      },
    }),
    display_on_mobile: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.header-button.display-on-mobile.label",
      },
    }),
    label_mobile: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.header-button.label-mobile.label",
        help: "pages.blocks.header-button.label-mobile.help",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.header-button.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.header-button.fieldset.button.label",
            fields: [
              "label",
              "link_url",
              "background_color",
              "text_color",
              "font_size",
              "font_weight",
            ],
          },
          {
            legend: "pages.blocks.header-button.fieldset.display.label",
            fields: [
              "display_on_desktop",
              "display_on_mobile",
              {
                prop: "label_mobile",
                visibleWhen: { display_on_mobile: true },
              },
            ],
          },
        ],
      },
    ],
  }),
);

export function headerButtonBlockDescriptor(): IBlockDescriptor;
export function headerButtonBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function headerButtonBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedHeaderButtonBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedHeaderButtonBlockDescriptor,
    headerButtonBlockProperties,
    fieldOrder,
  );
}

export {
  headerButtonBlockBlocDefinition,
  headerButtonBlockDashboardLayout,
  headerButtonBlockProperties,
  headerButtonBlockConfig,
  headerButtonBlockResolveConfig,
};

export type HeaderButtonBlockConfig = typeof headerButtonBlockConfig;
